import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { Plus, Search, X, Filter, AlertCircle } from 'lucide-react';
import PageTitle from '../../components/generics/page-title';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useServiceViewModel } from '../../view-model/professional/company/service-view-model';
import { useTranslation } from 'react-i18next';
import ServiceGrid from '../../components/services/service-grid';
import ServiceModal from '../../components/services/service-modal';
import OnboardingTour from '../onboarding/onboarding-tour';
import { SECTION_SERVICES } from '../../constants/onboarding-sections';
import ReviewTutorialButton from '../../components/onboarding/review-tutorial-button';


const Services = observer(() => {
  const viewModel = useServiceViewModel();
  const { t } = useTranslation();
  
  useEffect(() => {
    viewModel.getServices();
  }, []);

  return (
    <>
    <OnboardingTour section={SECTION_SERVICES} />
    <div className="h-full flex flex-col space-y-4 overflow-hidden">
      <PageTitle 
        title={t('services.title')}
        subtitle={t('services.subtitle')}
        actions={
          <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-4">
            <div className="relative flex-1 max-w-md services-filter-search">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <Search className="h-4 w-4 text-gray-500" />
              </div>
              <input
                type="text"
                className="pl-10 pr-10 py-2 w-full border border-gray-300 dark:border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-primary-dark dark:bg-gray-800"
                placeholder={t('services.placeholder') || 'Rechercher une prestation...'}
                value={viewModel.searchTerm}
                onChange={(e) => viewModel.filterServices(e.target.value, viewModel.statusFilter)}
              />
              {viewModel.searchTerm && (
                <button
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                  onClick={() => viewModel.filterServices('', viewModel.statusFilter)}
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              )}
            </div>
            <div className="flex justify-between items-center space-x-4 sm:flex-shrink-0">
              <div className="relative services-filter-status">
                <Button 
                  onClick={() => {
                    const nextFilter = viewModel.statusFilter === 'all' ? 'active' : viewModel.statusFilter === 'active' ? 'inactive' : 'all';
                    viewModel.filterServices(viewModel.searchTerm, nextFilter);
                  }}
                  variant="outline"
                  size="sm"
                  className="flex items-center"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {viewModel.statusFilter === 'all' ? t('services.statusFilter.all') : viewModel.statusFilter === 'active' ? t('services.statusFilter.active') : t('services.statusFilter.inactive')}
                </Button>
              </div>
              <Button 
                onClick={() => viewModel.openCreateModal()}
                className="inline-flex items-center services-add"
              >
                <Plus size={16} className="mr-2" />
                {t('services.add')}
              </Button>
            </div>
          </div>
        }
      />

      <div className="fixed bottom-6 z-50">
        <ReviewTutorialButton section={SECTION_SERVICES} />
      </div>

      {/* Alerte pour les services sans délai d'annulation */}
      {viewModel.shouldShowCancellationDeadlineAlert && (
        <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-semibold text-orange-900 dark:text-orange-100 mb-1">
              Configuration du délai d'annulation requise
            </h3>
            <p className="text-sm text-orange-800 dark:text-orange-200 mb-2">
              {viewModel.servicesWithoutCancellationDeadline.length} service{viewModel.servicesWithoutCancellationDeadline.length > 1 ? 's' : ''} n'{viewModel.servicesWithoutCancellationDeadline.length > 1 ? 'ont' : 'a'} pas de délai d'annulation configuré.
              Ce paramètre est important pour gérer les remboursements automatiques lors des annulations.
            </p>
            <p className="text-xs text-orange-700 dark:text-orange-300">
              Services concernés : {viewModel.servicesWithoutCancellationDeadline.map(s => s.name).join(', ')}
            </p>
          </div>
        </div>
      )}

      <Card className="flex-1 overflow-hidden flex flex-col">
        <CardHeader>
          <h2 className="text-lg font-semibold">{t('services.titleList')}</h2>
        </CardHeader>

        <CardContent className="flex-1 overflow-auto">
          {viewModel.isLoading ? (
            <div className="flex justify-center items-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : (
            <ServiceGrid />
          )}
        </CardContent>
      </Card>
      
      {/* Modal de service */}
      <ServiceModal />
    </div>
    </>
  );
});

export default Services;
